import { MailOutlined } from "@ant-design/icons";
import { Button, Input, InputNumber, message, Modal, Select, Space, Table, Tooltip, Typography } from "antd";
import ListTable from "common/ListTable";
import { getData, postData, putData } from "apiServices/common";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useEffect, useMemo, useRef, useState } from "react";
import { INITIAL_SAM_APPROVAL_PERMISSION, INITIAL_SAM_STORE_PERMISSION } from "routes/permissions";
import { commaSeparateNumber, hasPermission } from "utils/functions";

const { Option } = Select;

const BREAKDOWN_PASTE_FIELDS = ["style_no", "projected_qty", "considerable_days", "no_of_workers", "total_sam"];

const isEmptyBreakdownRow = (row) =>
  !row?.style_no &&
  row?.projected_qty === null &&
  row?.considerable_days === null &&
  row?.no_of_workers === null &&
  row?.total_sam === null;

const parseBreakdownPasteValue = (field, value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return field === "style_no" ? "" : null;
  }

  if (field === "style_no") {
    return trimmed;
  }

  const num = Number(trimmed.replace(/,/g, ""));
  return Number.isFinite(num) ? num : null;
};

const createBreakdownRowFromPaste = (cells, index) => {
  const row = { key: Date.now() + index + Math.random() };

  BREAKDOWN_PASTE_FIELDS.forEach((field, fieldIndex) => {
    row[field] = parseBreakdownPasteValue(field, cells[fieldIndex]);
  });

  return row;
};

const InitialSAMEntry = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [metaData, setMetaData] = useState({
    par_page: 10,
    total: 0,
  });
  const [showFilter, setShowFilter] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [buyerInquiryList, setBuyerInquiryList] = useState([]);
  const [selectedBuyerInquiryId, setSelectedBuyerInquiryId] = useState(null);
  const [breakdownRows, setBreakdownRows] = useState([
    {
      key: Date.now(),
      style_no: "",
      projected_qty: null,
      considerable_days: null,
      no_of_workers: null,
      total_sam: null,
    },
  ]);
  const [addModalSubmitting, setAddModalSubmitting] = useState(false);
  const [isPasteAreaFocused, setIsPasteAreaFocused] = useState(false);
  const breakdownPasteAreaRef = useRef(null);
  const [filters, setFilters] = useState({
    style_id: null,
    buying_office_id: null,
    buyer_id: null,
    sub_contract_factory_id: null,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [sendingMail, setSendingMail] = useState(false);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    columnWidth: 20,
  };

  const handleSendMail = async () => {
    if (selectedRowKeys.length === 0) {
      message.error("Please select at least one record.");
      return;
    }
    setSendingMail(true);
    try {
      const res = await postData("/api/initial-sam/send-multi-mail", {
        ids: selectedRowKeys,
      });
      if (res?.code === 200) {
        message.success(res?.message || "Mail sent successfully.");
        setSelectedRowKeys([]);
      }
    } catch (error) {
      message.error("Failed to send mail.");
    } finally {
      setSendingMail(false);
    }
  };

  const convertData = (originData) => {
    return originData.map((item) => ({
      key: item.id, // Unique key for table rows
      id: item.id,
      subcontract_status: item.subcontract_status,
      buyer: item.buyer_info?.name,
      buyer_id: item.buyer_id, // Include buyer_id for payload
      buying_office_factory: item.subcontract_status ? item.factory_info?.name : item.buying_office_info?.name, // Display factory name if subcontracted else buying office name
      buying_office_id: item.buying_office_id, // Include buying_office_id for payload
      style_no: item.style_inquiry_map?.style_info?.style_no,
      style_id: item.style_inquiry_map?.style_id, // Include style_id for payload
      order_qty: item.expected_order_quantity,
      total_sam: item.initial_sam?.total_sam || null,
      no_of_workers: item.initial_sam?.no_of_workers || null,
      inquiry_id: item.style_inquiry_map?.inquiry_id,
      sub_style_id: null,
      sub_contract_factory_id: item.sub_contract_factory_id,
      sales_contract_id: item.sales_contract_id || null,
      order_quantity: item.expected_order_quantity,
      initial_sam_id: item.initial_sam?.id || null,
      created_for: item.initial_sam?.operation_bulletin?.created_for || "Initial Sam",
      considerable_days: item.initial_sam?.considerable_days ?? null,
      note: item.initial_sam?.note || null,
    }));
  };

  // Fetch data based on filters
  const fetchData = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await getData("/api/initial-sams", null, {
        ...filters,
        page: currentPage,
      });
      setMetaData({
        ...metaData,
        total: res.data?.data?.total,
      });
      const formattedData = convertData(res.data?.data?.data || []);
      setData(formattedData || []);
      setSelectedRowKeys([]);
    } catch (error) {
      setFetchError("Could not load this list.");
      setData([]);
      message.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters, currentPage, refresh]);

  const fetchBuyerInquiries = async () => {
    const res = await getData("/api/buyer-inquiries", false, {
      per_page: 10,
      page: 1,
      is_multiple_style: "Yes",
      sam_ui: 1,
    });

    if (res?.data?.code === 200) {
      setBuyerInquiryList(res?.data?.data?.data || []);
    }
  };

  const openAddModal = async () => {
    setIsAddModalOpen(true);
    await fetchBuyerInquiries();
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setSelectedBuyerInquiryId(null);
    setBreakdownRows([
      {
        key: Date.now(),
        style_no: "",
        projected_qty: null,
        considerable_days: null,
        no_of_workers: null,
        total_sam: null,
      },
    ]);
  };

  const addBreakdownRow = () => {
    setBreakdownRows((prev) => [
      ...prev,
      {
        key: Date.now() + Math.random(),
        style_no: "",
        projected_qty: null,
        considerable_days: null,
        no_of_workers: null,
        total_sam: null,
      },
    ]);
  };

  const removeBreakdownRow = (key) => {
    setBreakdownRows((prev) => prev.filter((row) => row.key !== key));
  };

  const getBreakdownCopyData = (row) => ({
    style_no: row.style_no,
    projected_qty: row.projected_qty,
    considerable_days: row.considerable_days,
    no_of_workers: row.no_of_workers,
    total_sam: row.total_sam,
  });

  const copyBreakdownRow = (key) => {
    setBreakdownRows((prev) => {
      const index = prev.findIndex((row) => row.key === key);
      if (index < 0) return prev;

      const newRow = {
        key: Date.now() + Math.random(),
        ...getBreakdownCopyData(prev[index]),
      };

      return [...prev.slice(0, index + 1), newRow, ...prev.slice(index + 1)];
    });
  };

  const updateBreakdownCell = (key, field, value) => {
    setBreakdownRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };

  const handleBreakdownPaste = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const pastedData = (event.clipboardData || window.clipboardData)?.getData("Text");
    if (!pastedData?.trim()) {
      return;
    }

    const rawRows = pastedData
      .trim()
      .split(/\r?\n/)
      .map((row) => row.split("\t"));

    const pastedRows = rawRows
      .filter((row) => row.some((cell) => String(cell).trim()))
      .map((row, index) => createBreakdownRowFromPaste(row, index));

    if (!pastedRows.length) {
      message.warning("No valid data found in pasted content.");
      return;
    }

    setBreakdownRows((prev) => {
      const shouldReplace = prev.length === 1 && isEmptyBreakdownRow(prev[0]);
      return shouldReplace ? pastedRows : [...prev, ...pastedRows];
    });

    message.success(`${pastedRows.length} row(s) added from clipboard.`);
  };

  const submitBreakdowns = async () => {
    if (!selectedBuyerInquiryId) {
      message.error("Please select buyer inquiry");
      return;
    }

    if (!breakdownRows?.length) {
      message.error("Please add at least one row");
      return;
    }

    const hasInvalidRows = breakdownRows.some(
      (row) =>
        !row?.style_no ||
        row?.projected_qty === null ||
        row?.considerable_days === null ||
        row?.no_of_workers === null ||
        row?.total_sam === null,
    );

    if (hasInvalidRows) {
      message.error("Please fill all fields for all rows");
      return;
    }

    const payload = {
      breakdowns: breakdownRows.map((row) => ({
        style_no: row.style_no,
        projected_qty: Number(row.projected_qty),
        considerable_days: Number(row.considerable_days),
        no_of_workers: Number(row.no_of_workers),
        total_sam: Number(row.total_sam),
      })),
    };

    setAddModalSubmitting(true);
    const res = await postData(`/api/buyer-inquiry/store-multi-style-from-sam/${selectedBuyerInquiryId}`, payload);
    setAddModalSubmitting(false);

    if (res?.code === 200) {
      message.success(res?.message || "Saved successfully");
      closeAddModal();
      setRefresh((prev) => prev + 1);
    }
  };

  // Handle save
  const handleSave = async (record) => {
    if (!record.total_sam && !record.no_of_workers) {
      message.error("Please fill all fields");
      return;
    }
    const payload = {
      inquiry_id: record.inquiry_id,
      style_id: record.style_id,
      sub_style_id: record.sub_style_id,
      subcontract_status: record.subcontract_status,
      buyer_id: record.buyer_id,
      buying_office_id: record.buying_office_id,
      sub_contract_factory_id: record.sub_contract_factory_id,
      sales_contract_id: record.sales_contract_id,
      total_sam: record.total_sam,
      no_of_workers: record.no_of_workers,
      order_quantity: record.order_quantity,
      considerable_days: record.considerable_days,
      note: record.note,
    };

    if (record.initial_sam_id) {
      // Update existing record
      await putData(`/api/initial-sam/update/${record.initial_sam_id}`, payload);
    } else {
      // Create new record
      await postData("/api/initial-sam/store", payload);
    }
    // Refresh data
    setRefresh((prev) => prev + 1);
  };

  // Table columns
  const columns = [
    {
      title: "Buying Office / Factory",
      dataIndex: "buying_office_factory",
      key: "buying_office_factory",
    },
    {
      title: "Buyer",
      dataIndex: "buyer",
      key: "buyer",
    },
    {
      title: "Style No",
      dataIndex: "style_no",
      key: "style_no",
    },
    {
      title: "Order Qty",
      dataIndex: "order_qty",
      key: "order_qty",
      render: (text) => commaSeparateNumber(text),
      width: "7%",
      align: "right",
    },
    {
      title: "SAM",
      dataIndex: "total_sam",
      key: "total_sam",
      width: "8%",
      align: "left",
      render: (text, record) => (
        <InputNumber
          disabled={record?.created_for !== "Initial Sam"}
          style={{ width: "100%" }}
          value={text}
          placeholder="SAM"
          onChange={(value) =>
            setData((prev) => prev.map((item) => (item.key === record.key ? { ...item, total_sam: value } : item)))
          }
        />
      ),
    },
    {
      title: "MP",
      dataIndex: "no_of_workers",
      key: "no_of_workers",
      width: "7%",
      align: "left",
      render: (text, record) => (
        <InputNumber
          disabled={record?.created_for !== "Initial Sam"}
          placeholder="MP"
          style={{ width: "100%" }}
          value={text}
          onChange={(value) =>
            setData((prev) => prev.map((item) => (item.key === record.key ? { ...item, no_of_workers: value } : item)))
          }
        />
      ),
    },
    {
      title: "Considerable Days",
      dataIndex: "considerable_days",
      key: "considerable_days",
      width: "8%",
      render: (text, record) => (
        <InputNumber
          disabled={record?.created_for !== "Initial Sam"}
          placeholder="Days"
          style={{ width: "100%" }}
          value={text}
          min={0}
          precision={4}
          step={0.0001}
          onChange={(value) =>
            setData((prev) =>
              prev.map((item) => (item.key === record.key ? { ...item, considerable_days: value } : item)),
            )
          }
        />
      ),
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      width: "20%",
      render: (text, record) => (
        <Tooltip title={text}>
          <Input
            disabled={record?.created_for !== "Initial Sam"}
            placeholder="Note"
            style={{ width: "100%" }}
            value={text}
            onChange={(e) =>
              setData((prev) =>
                prev.map((item) => (item.key === record.key ? { ...item, note: e.target.value } : item)),
              )
            }
          />
        </Tooltip>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() => handleSave(record)}
          size="small"
          disabled={!hasPermission([INITIAL_SAM_STORE_PERMISSION]) || record?.created_for !== "Initial Sam"}
        >
          {record.initial_sam_id ? "Update" : "Add"}
        </Button>
      ),
    },
  ];

  const getFilterValue = (filterValues) => {
    setFilters(filterValues);
  };

  const breakdownColumns = [
    {
      title: "Style No",
      dataIndex: "style_no",
      key: "style_no",
      render: (_, row) => (
        <Input
          size="small"
          value={row?.style_no}
          onChange={(e) => updateBreakdownCell(row.key, "style_no", e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: "Projected Qty",
      dataIndex: "projected_qty",
      key: "projected_qty",
      render: (_, row) => (
        <InputNumber
          size="small"
          min={0}
          style={{ width: "100%" }}
          value={row?.projected_qty}
          onChange={(value) => updateBreakdownCell(row.key, "projected_qty", value)}
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: "Considerable Days",
      dataIndex: "considerable_days",
      key: "considerable_days",
      render: (_, row) => (
        <InputNumber
          size="small"
          min={0}
          precision={4}
          step={0.0001}
          style={{ width: "100%" }}
          value={row?.considerable_days}
          onChange={(value) => updateBreakdownCell(row.key, "considerable_days", value)}
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: "No Of Workers",
      dataIndex: "no_of_workers",
      key: "no_of_workers",
      render: (_, row) => (
        <InputNumber
          size="small"
          min={0}
          style={{ width: "100%" }}
          value={row?.no_of_workers}
          onChange={(value) => updateBreakdownCell(row.key, "no_of_workers", value)}
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: "Total SAM",
      dataIndex: "total_sam",
      key: "total_sam",
      render: (_, row) => (
        <InputNumber
          size="small"
          min={0}
          style={{ width: "100%" }}
          value={row?.total_sam}
          onChange={(value) => updateBreakdownCell(row.key, "total_sam", value)}
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      align: "center",
      render: (_, row) => (
        <Space size="small">
          <Button size="small" onClick={() => copyBreakdownRow(row.key)}>
            Copy
          </Button>
          <Button
            danger
            size="small"
            onClick={() => removeBreakdownRow(row?.key)}
            disabled={breakdownRows.length <= 1}
          >
            Remove
          </Button>
        </Space>
      ),
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={data}
        filterAPI={getFilterValue}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterName="initial_sam_entry"
      />
    ),
    [showFilter],
  );

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<MailOutlined />}
                disabled={
                  !hasPermission([INITIAL_SAM_APPROVAL_PERMISSION]) || selectedRowKeys.length === 0
                }
                onClick={handleSendMail}
                loading={sendingMail}
              >
                Send Mail{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ""}
              </Button>
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
                handleAddItemOpen={openAddModal}
                addButtonText="Add Multy Style"
              />
            </Space>
          }
          title={<Typography.Title level={5}>Initial SAM Entry</Typography.Title>}
        />
      }
      content={
        <>
          <style>{`
            .initial-sam-table .ant-table-selection-col {
              width: 40px !important;
            }
            .initial-sam-table .ant-table-selection-column {
              width: 40px !important;
              min-width: 40px !important;
              max-width: 40px !important;
              text-align: center !important;
              padding-left: 0px !important;
              padding-right: 0px !important;
            }
          `}</style>
          {memoizedCustomFilter}

          <Modal
            title="Add"
            open={isAddModalOpen}
            onCancel={closeAddModal}
            onOk={submitBreakdowns}
            width={1100}
            confirmLoading={addModalSubmitting}
            destroyOnClose
          >
            <div style={{ marginBottom: 12 }}>
              <Typography.Text style={{ display: "block", marginBottom: 6 }}>Buyer Inquiry</Typography.Text>
              <Select
                size="small"
                style={{ width: "100%" }}
                placeholder="Select Buyer Inquiry"
                value={selectedBuyerInquiryId}
                onChange={setSelectedBuyerInquiryId}
                showSearch
                optionFilterProp="children"
              >
                {buyerInquiryList?.map((item) => (
                  <Option key={item?.id} value={item?.id}>
                    {item?.inquiry_no || item?.reference_no || `Inquiry #${item?.id}`}
                  </Option>
                ))}
              </Select>
            </div>

            <Space style={{ marginBottom: 10 }}>
              <Button type="primary" size="small" onClick={addBreakdownRow}>
                Add Row
              </Button>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Click the table area below and paste from Excel (Ctrl+V) to add rows
              </Typography.Text>
            </Space>

            <div
              ref={breakdownPasteAreaRef}
              tabIndex={0}
              onPaste={handleBreakdownPaste}
              onClick={() => {
                breakdownPasteAreaRef.current?.focus();
                setIsPasteAreaFocused(true);
              }}
              onBlur={() => setIsPasteAreaFocused(false)}
              style={{
                border: isPasteAreaFocused ? "2px solid #1677ff" : "1px dashed #d9d9d9",
                borderRadius: 6,
                padding: 4,
                outline: "none",
              }}
            >
              <Table
                bordered
                size="small"
                rowKey="key"
                dataSource={breakdownRows}
                columns={breakdownColumns}
                pagination={false}
              />
            </div>
          </Modal>

          <div className="table" style={{ marginTop: 10 }}>
            <ListTable
              className="initial-sam-table"
              rowSelection={rowSelection}
              columns={columns}
              dataSource={data}
              loading={loading}
              error={fetchError}
              onRetry={() => fetchData()}
              rowKey="key"
              emptyText="No records to show yet."
              pagination={{
                current: currentPage,
                onChange: (page) => setCurrentPage(page),
                pageSize: 10,
                total: metaData.total,
                showTotal: (total) => `Total ${total} items`,
                showSizeChanger: false,
              }}
              bordered
            />
          </div>
        </>
      }
    />
  );
};

export default InitialSAMEntry;
